import { Injectable } from '@angular/core';
import { Message, MessageService } from 'primeng/api';
import { extractHttpErrorMessage } from '../../utils/http-error.util';

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

export interface ToastOptions {
  summary?: string;
  life?: number;
  sticky?: boolean;
  closable?: boolean;
  key?: string;
}

export interface ApiMessagePayload {
  success?: boolean;
  message?: string;
}

const DEFAULT_SUMMARIES: Record<ToastSeverity, string> = {
  success: 'Success',
  info: 'Info',
  warn: 'Heads up',
  error: 'Error',
};

const DEFAULT_LIFE: Record<ToastSeverity, number> = {
  success: 4000,
  info: 4500,
  warn: 5500,
  error: 7000,
};

/** Shorter times used on the prescription screen so toasts do not cover the consult. */
export const PRESCRIPTION_TOAST_LIFE: Record<ToastSeverity, number> = {
  success: 2000,
  info: 2000,
  warn: 2500,
  error: 3500,
};

const SEVERITY_ICONS: Record<ToastSeverity, string> = {
  success: 'pi pi-check-circle',
  info: 'pi pi-info-circle',
  warn: 'pi pi-exclamation-triangle',
  error: 'pi pi-times-circle',
};

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  /** Shared toast key — must match `<p-toast key="app">` in app shell. */
  readonly toastKey = 'app';

  private lifeCaps: Partial<Record<ToastSeverity, number>> | null = null;

  constructor(private messageService: MessageService) {}

  /**
   * Cap how long toasts stay visible. Returns a function that restores the
   * previous cap, so a screen can apply this only while it is open.
   */
  useLifeCaps(caps: Partial<Record<ToastSeverity, number>>): () => void {
    const previous = this.lifeCaps;
    this.lifeCaps = caps;
    return () => {
      this.lifeCaps = previous;
    };
  }

  show(severity: ToastSeverity, detail: string, options: ToastOptions = {}): void {
    const text = (detail ?? '').trim();
    if (!text) {
      return;
    }

    this.messageService.add({
      key: options.key ?? this.toastKey,
      severity,
      summary: options.summary ?? DEFAULT_SUMMARIES[severity],
      detail: text,
      life: this.resolveLife(severity, options.life, options.sticky),
      sticky: options.sticky ?? false,
      closable: options.closable ?? true,
      icon: SEVERITY_ICONS[severity],
    });
  }

  success(detail: string, summary?: string, options?: Omit<ToastOptions, 'summary'>): void {
    this.show('success', detail, { ...options, summary: summary ?? DEFAULT_SUMMARIES.success });
  }

  error(detail: string, summary?: string, options?: Omit<ToastOptions, 'summary'>): void {
    this.show('error', detail, { ...options, summary: summary ?? DEFAULT_SUMMARIES.error });
  }

  warn(detail: string, summary?: string, options?: Omit<ToastOptions, 'summary'>): void {
    this.show('warn', detail, { ...options, summary: summary ?? DEFAULT_SUMMARIES.warn });
  }

  info(detail: string, summary?: string, options?: Omit<ToastOptions, 'summary'>): void {
    this.show('info', detail, { ...options, summary: summary ?? DEFAULT_SUMMARIES.info });
  }

  /** Show success or error based on a typical `{ success, message }` API body. */
  fromApiResponse(
    res: ApiMessagePayload | null | undefined,
    options: {
      successSummary?: string;
      errorSummary?: string;
      successFallback?: string;
      errorFallback?: string;
    } = {},
  ): void {
    const message = res?.message?.trim();
    if (res?.success) {
      this.success(message || options.successFallback || 'Saved successfully.', options.successSummary);
      return;
    }
    this.error(message || options.errorFallback || 'Request failed.', options.errorSummary);
  }

  httpError(error: unknown, fallback?: string, summary?: string): void {
    this.error(extractHttpErrorMessage(error, fallback), summary);
  }

  /** Low-level passthrough for legacy `messageService.add({ ... })` call sites. */
  add(message: Message): void {
    const severity = (message.severity ?? 'info') as ToastSeverity;
    const { life, ...rest } = message;
    this.messageService.add({
      ...rest,
      key: message.key ?? this.toastKey,
      closable: message.closable ?? true,
      icon: message.icon ?? SEVERITY_ICONS[severity],
      severity,
      life: this.resolveLife(severity, life, message.sticky),
    });
  }

  private resolveLife(
    severity: ToastSeverity,
    requested: number | undefined,
    sticky?: boolean,
  ): number | undefined {
    if (sticky) {
      return undefined;
    }
    const base = requested ?? DEFAULT_LIFE[severity] ?? DEFAULT_LIFE.info;
    const cap = this.lifeCaps?.[severity];
    return cap == null ? base : Math.min(base, cap);
  }

  clear(key = this.toastKey): void {
    this.messageService.clear(key);
  }
}
